import { ApiCurrency, ApiProject } from "@incendium/api";
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,

  Typography,
} from "@mui/material";
import { addProject, updateProject } from "Actions";
import { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router";
import { ValidatorForm } from "react-material-ui-form-validator";
import CustomDialog from "Components/CustomDialog/CustomDialog";
import { GenericDialoagProps } from "Interfaces";
import { ProjectDomains, saveProject } from "features/project";
import { useNotification } from "Hooks";
import ProjectFormInner from "./ProjectFormInner";

interface IProjectDialogProps extends GenericDialoagProps {
  project?: ApiProject;
}

function ProjectDialog({ project, open, setOpen }: IProjectDialogProps) {
  const [name, setName] = useState(project?.name || "");
  const [domains, setDomains] = useState(
    (project?.domains || []).filter((d) => d.isPrimary) || []
  );
  const [overview, setOverview] = useState(project?.overview || "");
  const [timezone, setTimezone] = useState(project?.timezone || "");
  const [currency, setCurrency] = useState(
    project?.currency || ApiCurrency.USD
  );
  const [disabled, setDisabled] = useState(false);
  const dispatch = useDispatch();
  const { clientId } = useParams<{ clientId: string }>();
  const { showSuccessNotification, showErrorNotification } = useNotification();

  const onClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const onSubmit = useCallback(async () => {
    setDisabled(true);
    try {
      const res = await saveProject(parseInt(clientId), {
        id: project?.id,
        name,
        overview,
        timezone,
        domains,
        currency,
      });
      if (project?.id) {
        dispatch(updateProject(res));
      } else {
        dispatch(addProject(res));
      }
      showSuccessNotification(`${res.name} Saved`);
    } catch (e) {
      showErrorNotification("Internal Server Error");
    } finally {
      setDisabled(false);
      onClose();
    }
  }, [
    project,
    clientId,
    showSuccessNotification,
    showErrorNotification,
    dispatch,
    name,
    onClose,
    overview,
    timezone,
    domains,
    currency,
  ]);

  return (
    <CustomDialog onClose={onClose} fullWidth maxWidth="sm" open={open}>
      <ValidatorForm onSubmit={onSubmit}>
        <DialogTitle>
          {project && project.id && project.id > 0
            ? `Update ${project.name}`
            : "Add a new Project"}
        </DialogTitle>
        <DialogContent>
          <ProjectFormInner
            name={name}
            setName={setName}
            timezone={timezone}
            setTimezone={setTimezone}
            currency={currency}
            setCurrency={setCurrency}
            overview={overview}
            setOverview={setOverview}
            disabled={disabled}
          />
        </DialogContent>
        <Divider />

        <DialogContent>
          <Typography variant="subtitle2">
            Manage your projects domains
          </Typography>
          <Typography variant="body2" mb={3}>
            These are the domain you shall run incendium from
          </Typography>
          <ProjectDomains domains={domains} setDomains={setDomains} />
        </DialogContent>
        <DialogActions>
          <Button disabled={disabled} onClick={onClose} color="secondary">
            Cancel
          </Button>
          <Button
            disabled={disabled}
            variant="contained"
            color="primary"
            type="submit"
          >
            {project ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </ValidatorForm>
    </CustomDialog>
  );
}

export default ProjectDialog;
